import { useAuth } from '../../../components/providers/AuthProvider';
import { useDashboardContext } from '../../../components/providers/DashboardProvider';
import { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import BessBillComparison from './BessBillComparison';
import RawDataPanel from '../RawDataPanel';


const UtilityBillPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.profile?.user_type === 'admin';

  const {
    fetchClusterUtilityBillData,
    fetchClusterUtilityBillPeriods, 
    utilityBillData, 
    utilityBillPeriods, 
    loading: isLoading
  } = useDashboardContext();

  const [billData, setBillData] = useState(null);
  const [billPeriods, setBillPeriods] = useState([]);
  const [dataValid, setDataValid] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const [selectedMonth, setSelectedMonth] = useState(5); // May
  const [selectedYear, setSelectedYear] = useState(2026);

  // Derive billData from context reactively, instead of reading utilityBillData
  // right after an await inside refetch (that reads a stale, pre-update value
  // captured by the closure — the context hasn't re-rendered this component
  // yet at that point). This effect re-runs whenever the context value
  // actually changes, however long the fetch takes.
  useEffect(() => {
    if (utilityBillData && utilityBillData.comparison) {
      try {
        const parsed = JSON.parse(utilityBillData.comparison);
        setBillData(parsed);
        setDataValid(true);
        setError(null);
      } catch (e) {
        setBillData(null);
        setDataValid(false);
        setError('Could not parse the utility bill data.');
      }
    } else {
      setBillData(null);
      setDataValid(false);
    }
  }, [utilityBillData]);

  useEffect(() => {
    console.log('Utility bill periods updated:', utilityBillPeriods);
  }, [utilityBillPeriods]);

  const refetch = async (month, year) => {
    setIsRefreshing(true);
    setError(null);
    try {
      await fetchClusterUtilityBillPeriods();
      await fetchClusterUtilityBillData(year, month);
      // billData updates via the useEffect above once utilityBillData changes.
    } catch (e) {
      setError(e?.message || 'Failed to fetch utility bill data.');
    } finally {
      setIsRefreshing(false);
    }
  };

  // Initial load: fetch available periods, then auto-select the latest one.
  useEffect(() => {
    fetchClusterUtilityBillPeriods().then((periods) => {
      if (!periods || periods.length === 0) return;
      const latest = periods.reduce((best, p) =>
        p.year > best.year || (p.year === best.year && p.month > best.month) ? p : best
      );
      setSelectedYear(latest.year);
      setSelectedMonth(latest.month);
      fetchClusterUtilityBillData(latest.year, latest.month);
    });
  }, []);

  const handleMonthChange = (month) => {
    setSelectedMonth(month);
    refetch(month, selectedYear);
  };

  const handleYearChange = (year) => {
    setSelectedYear(year);
    refetch(selectedMonth, year);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        pt: 2,
        gap: 2,
      }}
    >
      <BessBillComparison
        data={billData}
        isLoading={isLoading}
        isRefreshing={isRefreshing}
        error={error}
        selectedMonth={selectedMonth}
        selectedYear={selectedYear}
        availablePeriods={utilityBillPeriods}
        onMonthChange={handleMonthChange}
        onYearChange={handleYearChange}
        onRefresh={() => refetch(selectedMonth, selectedYear)}
      />

      {utilityBillData && isAdmin && <RawDataPanel data={utilityBillData} />}
    </Box>
  );
};

export default UtilityBillPage;